import React from 'react';

import clsx from 'clsx';
import dynamic from 'next/dynamic';
import { DatePickerProps } from 'react-date-picker';
import { createUseStyles } from 'react-jss';

import Text from 'DesignSystem2/Text';
import { HeaderFontFeatureSettings } from 'DesignSystem2/Text';

import { Border, Palette, Radius, Spacing } from '../Theme';

const DatePicker = dynamic(() => import('react-date-picker'));

const useStyles = createUseStyles({
    container: {
        padding: [Spacing(1.5), Spacing(2)],
        width: 'calc(100% + 2px)',
        display: 'block',
        boxSizing: 'border-box',
        textAlign: 'start',
        border: Border('transparent'),
        margin: '-1px',
        '& .react-date-picker__wrapper': {
            border: 'none',
            height: 24,
        },
        '& .picker__inputGroup__year': {
            width: 40,
        },
        '& .picker__inputGroup__month': {
            minWidth: 18,
        },
        '& .picker__inputGroup__day': {
            minWidth: 18,
        },
        '& .react-date-picker__inputGroup__input': {
            color: Palette.Text.x10,
        },
        '& .react-date-picker__inputGroup__input--hasLeadingZero': {
            minWidth: 9,
        },
        '& .react-date-picker__inputGroup__input:invalid': {
            background: 'transparent',
        },
    },
    containerEnabled: {
        composes: '$container',
        cursor: 'text',
        borderRadius: Radius.x30,
        transition: 'all 0.2s ease-in-out',
        outline: 'transparent',
        '&:hover': {
            border: Border(Palette.Fill.x30),
        },
        '&:focus-within': {
            border: Border('#00D991', false),
            outline: '1px solid #00D991',
        },
    },
    containerDisabled: {
        composes: '$container',
        backgroundColor: Palette.Fill.x50,
    },
    errorSquare: {
        display: 'inline-block',
        borderRadius: Radius.x10,
        height: 8,
        width: 8,
        marginRight: Spacing(1),
        backgroundColor: Palette.Critical.x10,
    },
    input: {
        fontFamily: 'var(--inter-font)',
        fontFeatureSettings: HeaderFontFeatureSettings,
        fontSize: '16px',
        lineHeight: '24px',
        color: Palette.Text.x10,
        fontWeight: 500,
        border: 0,
        outline: 0,
        padding: 0,
        backgroundColor: 'transparent',
        width: '100%',
        marginLeft: -3,
        '&::placeholder': {
            color: Palette.Text.x30,
            fontFeatureSettings: HeaderFontFeatureSettings,
            fontFamily: 'var(--inter-font)',
        },
        '&:focus-within': {
            border: 'none',
            outline: 'none',
        },
        '& *:not(:first-child)': {
            '&:focus-within': {
                border: 'none',
                outline: 'none',
            },
        },
    },
    inputEnabled: {
        composes: '$input',
        color: Palette.Text.x10,
    },
    inputDisabled: {
        composes: '$input',
        color: Palette.Text.x20,
    },
});

interface BaseDateInputProps extends DatePickerProps {
    labelText?: string;
}
interface ErrorDateInputProps extends BaseDateInputProps {
    errorText?: string;
    helperText?: never;
}
interface HelperDateInputProps extends BaseDateInputProps {
    errorText?: never;
    helperText?: string;
}
type DateInputProps = ErrorDateInputProps | HelperDateInputProps;

const DateInput = ({
    labelText = '',
    errorText = '',
    helperText = '',
    className,
    format,
    ...datePickerProps
}: DateInputProps): JSX.Element => {
    const classes = useStyles();
    const { disabled } = datePickerProps;

    const label = !!labelText && (
        <Text.Body.x20 color={disabled ? Palette.Text.x20 : Palette.Text.x10}>{labelText}</Text.Body.x20>
    );
    const helper = !!helperText && <Text.Body.x20 color={Palette.Text.x20}>{helperText}</Text.Body.x20>;
    const error = !!errorText && (
        <span>
            <span className={classes.errorSquare} />
            <Text.Body.x20 color={Palette.Critical.x10}>{errorText}</Text.Body.x20>
        </span>
    );
    return (
        <label
            className={clsx(className, disabled ? classes.containerDisabled : classes.containerEnabled)}
            onClick={(e): void => e.preventDefault()}
        >
            {!!labelText && (
                <span style={{ display: 'block', marginBottom: Spacing(0.25) }} className={'opacity-50'}>
                    {label}
                </span>
            )}
            <DatePicker
                className={clsx(classes.input, disabled ? classes.inputDisabled : classes.inputEnabled)}
                disabled={disabled}
                clearIcon={null}
                dayPlaceholder={'DD'}
                monthPlaceholder={'MM'}
                yearPlaceholder={'YYYY'}
                format={format || 'MM/dd/y'}
                {...datePickerProps}
            />
            {(!!helper || !!error) && (
                <span style={{ display: 'block', marginTop: Spacing(0.25) }}>
                    {helper} {error}
                </span>
            )}
        </label>
    );
};

export default DateInput;
